// src/pages/ModelPage.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Table, Typography, Space, Tag } from 'antd';

type ColumnsType<T> = any; // if you have AntD types globally, use ColumnsType<T> from 'antd/es/table'

interface ModelVersionRow {
  id: string;                 // "Name@Version"
  version: number;
  stage: string;
  status: string;
  created_at: string;
  created_by: string;
  model_type: string;
  dataset: string;
//   accuracy: number;           // 0..1
  precision?: number | null;
  recall?: number | null;
//   map50?: number | null;
//   map50_95?: number | null;
  notes?: string | null;
  run_id?: string | null;
  experiment_id?: string | null;
  mlflow_url?: string | null;
  artifact_uri?: string | null;
}

interface ModelDetailResponse {
  model_name: string;
  description?: string | null;
  latest_version?: number | null;
  items: ModelVersionRow[];
  total_count: number;
  total_pages: number;
}

const ModelPage: React.FC = () => {
  const { modelId } = useParams<{ modelId: string }>();
  const modelName = useMemo(
    () => decodeURIComponent(modelId || '').split('@')[0],
    [modelId]
  );

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<ModelDetailResponse | null>(null);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/api/models/${encodeURIComponent(modelName)}`,
          { credentials: 'include' }
        );
        if (!res.ok) throw new Error('Failed to load model');
        const json = await res.json();
        if (active) setData(json);
      } catch (e) {
        console.error(e);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [modelName]);

  const columns: ColumnsType<ModelVersionRow> = [
    { title: 'Version', dataIndex: 'version', key: 'version', sorter: (a: any, b: any) => a.version - b.version, defaultSortOrder: 'descend' },
    { title: 'Stage', dataIndex: 'stage', key: 'stage', render: (s: string) => s ? <Tag color="blue">{s}</Tag> : '-' },
    { title: 'Status', dataIndex: 'status', key: 'status' },
    { title: 'Created', dataIndex: 'created_at', key: 'created_at', render: (v: string) => v ? new Date(v).toLocaleString() : '-' },
    { title: 'Recall', dataIndex: 'recall', key: 'recall', render: (v: number | null | undefined) => v != null ? `${(v * 100).toFixed(2)}%` : '-' },
    { title: 'Precision', dataIndex: 'precision', key: 'precision', render: (v: number | null | undefined) => v != null ? `${(v * 100).toFixed(2)}%` : '-' },
    { title: 'Dataset', dataIndex: 'dataset', key: 'dataset' },
    { title: 'Type', dataIndex: 'model_type', key: 'model_type' },
    // {
    //   title: 'Actions',
    //   key: 'actions',
    //   render: (_: any, r: ModelVersionRow) => (
    //     <Space>
    //       {r.mlflow_url && <a href={r.mlflow_url} target="_blank" rel="noreferrer">Open in MLflow</a>}
    //       {r.artifact_uri && <a href={r.artifact_uri} target="_blank" rel="noreferrer">Artifacts</a>}
    //     </Space>
    //   )
    // }
  ];

  return (
    <div className="p-6">
      <Typography.Title level={3} style={{ color: 'white' }}>
        {data?.model_name || modelName}
        {data?.latest_version ? <span style={{ marginLeft: 12, fontSize: 14, color: '#9ca3af' }}>
          latest: v{data.latest_version}
        </span> : null}
      </Typography.Title>
      {data?.description && (
        <Typography.Paragraph style={{ color: 'white' }}>
          {data.description}
        </Typography.Paragraph>
      )}

      <Table<ModelVersionRow>
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={data?.items || []}
        pagination={{ pageSize: 20 }}
      />
    </div>
  );
};

export default ModelPage;
